import React from 'react';
import { LogOut, Menu } from 'lucide-react';
import {
  StyledTopBarContainer,
  StyledMenuButton,
  StyledUserInfoContainer,
  StyledUserInfoRow,
  StyledUserName,
  StyledAvatarContainer,
  StyledChevron,
  StyledLogoutButton,
  StyledPopupContainer,
  StyledPopupContent,
} from './TopBar/TopBar.styled';
import { Button, Modal } from './ui';
import {
  StyledActionButtons,
  StyledConfirmationContent,
  StyledConfirmationText,
  StyledLogoutConfirmation,
} from './Sidebar/LogoutConfirmation.styled';
import { rfpAuthService } from '../services/rfpAuthService';

interface RFPTopBarProps {
  onToggleSidebar: () => void;
}

// Dummy RFP user data
const dummyRFPUser = {
  name: 'RFP User',
  email: 'rfp.user@example.com',
};

const RFPTopBar: React.FC<RFPTopBarProps> = ({ onToggleSidebar }) => {
  const [isPopupOpen, setIsOpenPopup] = React.useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const popupRef = React.useRef<HTMLDivElement>(null);
  
  // Check if user is authenticated
  const isAuthenticated = rfpAuthService.hasRfpToken();

  // Handle clicks outside the popup
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setIsOpenPopup(false);
      }
    };

    if (isPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupOpen]);

  const handleLogout = () => {
    setIsLoggingOut(true);
    try {
      // Clear RFP token from session storage
      sessionStorage.removeItem('aloRfpToken');
      // Redirect to home
      window.location.href = '/login';
    } catch (error) {
      console.error('Log out failed:', error);
      window.location.href = '/login';
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleConfirmLogout = () => {
    handleLogout();
  };

  const handleCancelLogout = () => {
    if (!isLoggingOut) {
      setIsLogoutModalOpen(false);
    }
  };

  return (
    <StyledTopBarContainer>
      <div>
        <StyledMenuButton onClick={onToggleSidebar} aria-label="Toggle sidebar">
          <Menu size={24} />
        </StyledMenuButton>
      </div>

      {isAuthenticated && (
        <StyledUserInfoContainer ref={popupRef}>
          {/* User Info */}
          <StyledUserInfoRow
            onClick={() => {
              setIsOpenPopup(!isPopupOpen);
            }}
          >
            <StyledAvatarContainer>
              {dummyRFPUser.name.charAt(0).toUpperCase()}
            </StyledAvatarContainer>
            <StyledChevron $isExpanded={isPopupOpen} size={20} />
          </StyledUserInfoRow>

          {/* Popup opens on user avatar click */}
          {isPopupOpen && (
          <StyledPopupContainer>
            <StyledPopupContent>
              <StyledUserName>{dummyRFPUser.name}</StyledUserName>
              {/* <StyledUserEmail>{dummyRFPUser.email}</StyledUserEmail>
              <StyledProfileItem>
                <span className="p-1 bg-gray-50 rounded-full">
                  <UserIcon size={20} />
                </span>
                Profile
              </StyledProfileItem> */}
            </StyledPopupContent>
            <StyledLogoutButton onClick={() => setIsLogoutModalOpen(true)}>
              <LogOut size={20} />
              <span>Log out</span>
            </StyledLogoutButton>
          </StyledPopupContainer>
        )}

        {/* Logout Confirmation Modal */}
        <Modal
          isOpen={isLogoutModalOpen}
          onClose={handleCancelLogout}
          title="Confirm Log out"
          size="sm"
          closeOnBackdrop={!isLoggingOut}
        >
          <StyledLogoutConfirmation>
            <StyledConfirmationContent>
              <h3>Are you sure you want to log out?</h3>
              <StyledConfirmationText>
                You will be redirected to the home page.
              </StyledConfirmationText>
            </StyledConfirmationContent>

            <StyledActionButtons>
              <Button
                variant="outline"
                onClick={handleCancelLogout}
                disabled={isLoggingOut}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
              >
                {isLoggingOut ? 'Wait...' : 'Log out'}
              </Button>
            </StyledActionButtons>
          </StyledLogoutConfirmation>
        </Modal>
      </StyledUserInfoContainer>
      )}
    </StyledTopBarContainer>
  );
};

export default RFPTopBar;
